// useCartLogic.js
import { computed, onMounted, ref, watch } from 'vue'
import { useCartStore } from '../stores/cartStore'
import { useTeamStore } from '../stores/teamStore'
import { useRoute, useRouter } from 'vue-router'
import { persistStore } from '../stores/storeHelpers'

function slugify(value) {
    // Convert to string and trim
    let str = String(value).trim()

    // Convert accented characters to ASCII
    str = str.normalize("NFKD").replace(/[\u0300-\u036f]/g, "")

    // Convert to lowercase
    str = str.toLowerCase()

    // Remove all characters that are not alphanumeric, hyphens, or spaces
    str = str.replace(/[^\w\s-]/g, '')

    // Replace spaces and repeated dashes with a single dash
    str = str.replace(/[-\s]+/g, '-')

    // Remove leading/trailing dashes and underscores
    str = str.replace(/^[-_]+|[-_]+$/g, '')

    return str
}

export function useCartLogic(cartSlug) {
    const cartStore = useCartStore()
    const teamStore = useTeamStore()

    const routesLoaded = ref(false)
    const route = useRoute()
    const router = useRouter()

    const cartData = computed(() => cartStore.cartData)
    const currentTeam = computed(() => teamStore.currentTeam)
    const isLoadingTeam = computed(() => teamStore.isLoading)

    const restoreStateOnRefresh = () => {
        const currentPath = window.location.pathname
        router.push({ path: currentPath, query: { ...route.query } })
    }

    // Watch the cartSlug route parameter
    watch(
        () => route.params.cartSlug,
        async (newCartSlug, oldCartSlug) => {
            if (newCartSlug !== oldCartSlug) {
                console.log('Cart slug changed:', newCartSlug)
                const companyUuid = route.query.company_uuid || null
                cartStore.reloadCartData({ slug: newCartSlug, companyUuid })
                cartStore.clearAllFields('cartForm')
            }
        },
    )

    // Watch corporation name changes
    watch(() => cartData.value?.initialFormData?.corporation_name, async (newValue, oldValue) => {
        if (newValue && newValue !== oldValue) {
            const teamSlug = slugify(newValue)
            await teamStore.getTeamBySlug(teamSlug)
        }
    })

    // Watch for team slug changes in the route
    watch(() => route.params.teamSlug, async (newValue, oldValue) => {
        if (newValue && newValue !== oldValue) {
            await teamStore.getTeamBySlug(newValue)
        }
    })

    const setupRouter = async () => {
        restoreStateOnRefresh()
    }

    onMounted(async () => {
        const companyUuid = route.query.company_uuid || null
        await cartStore.loadCartData(cartSlug, companyUuid)
        if (cartStore.cartData?.cartSteps) {
            await setupRouter()
            routesLoaded.value = true
        } else {
            console.error('Cart data not loaded')
        }
        persistStore(cartStore, cartSlug)
        cartStore.setIsLoading(false)
    })

    const cartErrorMessage = computed(() => {
        if (!cartStore.cartData || !cartStore.cartData.initialFormData) {
            return 'Cart data is not available'
        }
        return cartStore.cartData.initialFormData.error
            ? 'Company Information Not Found- Email support@complianceclerk.com or use the green help chat in bottom right for help'
            : null
    })

    return {
        cartData,
        routesLoaded,
        cartErrorMessage,
        cartStore,
        router,
        currentTeam,
        isLoadingTeam,
        teamStore
    }
}